import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BackButton from "@/components/BackButton";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Download, Trash2, UserRound, Mail, Shield, Loader2 } from "lucide-react";

const ROLE_LABEL = {
    pubblico: "Cliente",
    trade: "Trade",
    vip: "VIP",
    staff: "Staff",
    admin: "Amministratore",
};

export default function Account() {
    const { user, loading, logout, refresh } = useAuth();
    const navigate = useNavigate();
    const [me, setMe] = useState(null);
    const [savingConsents, setSavingConsents] = useState(false);
    const [marketing, setMarketing] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [deletePassword, setDeletePassword] = useState("");
    const [deleteError, setDeleteError] = useState(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (loading) return;
        if (!user) {
            navigate("/login?next=/account");
            return;
        }
        api.get("/auth/me").then(({ data }) => {
            setMe(data);
            setMarketing(!!data.marketing_consent);
        }).catch(() => toast.error("Errore nel caricamento del profilo"));
    }, [user, loading, navigate]);

    const saveConsents = async (newVal) => {
        setSavingConsents(true);
        try {
            await api.put("/auth/me/consents", { marketing_consent: newVal });
            setMarketing(newVal);
            toast.success(newVal
                ? "Consenso marketing attivato"
                : "Consenso marketing revocato");
            const { data } = await api.get("/auth/me");
            setMe(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
            setMarketing(!newVal);
        } finally {
            setSavingConsents(false);
        }
    };

    const exportData = async () => {
        setExporting(true);
        try {
            const { data } = await api.get("/auth/me/export");
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `casa-orterie-dati-${(user?.email || "utente").replace(/[^a-z0-9]/gi, "_")}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success("Esportazione completata");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        } finally {
            setExporting(false);
        }
    };

    const deleteAccount = async () => {
        setDeleteError(null);
        if (!deletePassword) {
            setDeleteError("Inserisci la password per confermare.");
            return;
        }
        setDeleting(true);
        try {
            await api.delete("/auth/me", { data: { password: deletePassword } });
            toast.success("Account cancellato. Grazie per essere stato con noi.");
            logout();
            await refresh();
            navigate("/");
        } catch (err) {
            setDeleteError(formatApiErrorDetail(err.response?.data?.detail) || "Errore durante la cancellazione.");
        } finally {
            setDeleting(false);
        }
    };

    if (!me) {
        return (
            <div className="co-container py-14 text-center text-[hsl(var(--muted-foreground))]">
                Caricamento…
            </div>
        );
    }

    return (
        <main className="co-container py-10 sm:py-14 max-w-2xl" data-testid="account-page">
            <div className="mb-4"><BackButton testid="account-back-btn" /></div>
            <span className="co-caption">Area riservata</span>
            <h1 className="font-serif text-4xl mt-2 mb-2">Il mio account</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-8">
                Gestisci i tuoi consensi, esporta i tuoi dati o cancella definitivamente
                l'account. Le modifiche entrano subito in vigore.
            </p>

            <section className="mb-8 bg-white border border-[hsl(var(--border))] rounded-sm p-5">
                <div className="flex items-center gap-2 mb-4">
                    <UserRound className="w-4 h-4 text-[hsl(var(--primary))]" strokeWidth={1.5} />
                    <h2 className="text-xl">I miei dati</h2>
                </div>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                        <dt className="co-caption text-xs">Email</dt>
                        <dd data-testid="account-email">{me.email}</dd>
                    </div>
                    <div>
                        <dt className="co-caption text-xs">Ruolo</dt>
                        <dd data-testid="account-role">{ROLE_LABEL[me.role] || me.role}</dd>
                    </div>
                    <div>
                        <dt className="co-caption text-xs">Email verificata</dt>
                        <dd data-testid="account-verified">{me.email_verified ? "Sì" : "In attesa"}</dd>
                    </div>
                    <div>
                        <dt className="co-caption text-xs">Registrato dal</dt>
                        <dd>{typeof me.created_at === "string" ? me.created_at.slice(0, 10) : "—"}</dd>
                    </div>
                </dl>
            </section>

            <section className="mb-8 bg-white border border-[hsl(var(--border))] rounded-sm p-5">
                <div className="flex items-center gap-2 mb-4">
                    <Shield className="w-4 h-4 text-[hsl(var(--primary))]" strokeWidth={1.5} />
                    <h2 className="text-xl">Consensi</h2>
                </div>
                <div className="space-y-4 text-sm">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <div>Informativa privacy</div>
                            <div className="text-xs text-[hsl(var(--muted-foreground))]">
                                Accettata alla registrazione. Puoi revocarla solo cancellando l'account.
                                <Link to="/privacy" className="ml-1 underline text-[hsl(var(--primary))]">Leggi il testo</Link>
                            </div>
                        </div>
                        <span className="co-badge-neutral" data-testid="account-privacy-status">Accettata</span>
                    </div>
                    <div className="flex items-start justify-between gap-4 pt-4 border-t border-[hsl(var(--border))]">
                        <div>
                            <div>Comunicazioni promozionali</div>
                            <div className="text-xs text-[hsl(var(--muted-foreground))]">
                                Ricevi novità della cantina, eventi e degustazioni.
                            </div>
                        </div>
                        <Switch
                            checked={marketing}
                            disabled={savingConsents}
                            onCheckedChange={saveConsents}
                            data-testid="account-marketing-toggle"
                        />
                    </div>
                </div>
            </section>

            <section className="mb-8 bg-white border border-[hsl(var(--border))] rounded-sm p-5">
                <div className="flex items-center gap-2 mb-3">
                    <Download className="w-4 h-4 text-[hsl(var(--primary))]" strokeWidth={1.5} />
                    <h2 className="text-xl">Esporta i miei dati</h2>
                </div>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">
                    Scarichi un file JSON con tutti i dati personali che conserviamo — profilo,
                    consensi e log delle email che ti abbiamo inviato.
                </p>
                <button
                    onClick={exportData}
                    disabled={exporting}
                    className="co-btn-ghost text-sm inline-flex items-center gap-2"
                    data-testid="account-export-btn"
                >
                    {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" strokeWidth={1.5} />}
                    Scarica JSON
                </button>
            </section>

            <section className="mb-8 bg-white border border-[hsl(var(--destructive))]/40 rounded-sm p-5">
                <div className="flex items-center gap-2 mb-3">
                    <Trash2 className="w-4 h-4 text-[hsl(var(--destructive))]" strokeWidth={1.5} />
                    <h2 className="text-xl text-[hsl(var(--destructive))]">Cancella il mio account</h2>
                </div>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">
                    Rimozione definitiva del profilo e dei dati personali associati. L'operazione
                    è irreversibile. Riceverai una email di conferma. Un registro anonimo (hash
                    irreversibile) è conservato per finalità di accountability GDPR.
                </p>
                <AlertDialog onOpenChange={(open) => { if (!open) { setDeletePassword(""); setDeleteError(null); } }}>
                    <AlertDialogTrigger asChild>
                        <button className="co-btn-primary bg-[hsl(var(--destructive))] hover:opacity-90 text-sm" data-testid="account-delete-open-btn">
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} /> Cancella account
                        </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid="account-delete-dialog">
                        <AlertDialogHeader>
                            <AlertDialogTitle>Confermi la cancellazione?</AlertDialogTitle>
                            <AlertDialogDescription>
                                Inserisci la tua password per confermare. L'operazione non è reversibile.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <div className="mt-4">
                            <Label className="co-caption mb-2 block">Password attuale</Label>
                            <Input
                                type="password"
                                value={deletePassword}
                                onChange={(e) => setDeletePassword(e.target.value)}
                                autoComplete="current-password"
                                data-testid="account-delete-password-input"
                            />
                            {deleteError && (
                                <p className="mt-2 text-sm text-[hsl(var(--destructive))]" data-testid="account-delete-error">
                                    {deleteError}
                                </p>
                            )}
                        </div>
                        <AlertDialogFooter>
                            <AlertDialogCancel data-testid="account-delete-cancel">Annulla</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={(e) => { e.preventDefault(); deleteAccount(); }}
                                disabled={deleting}
                                className="bg-[hsl(var(--destructive))] hover:opacity-90"
                                data-testid="account-delete-confirm"
                            >
                                {deleting ? "Cancellazione…" : "Sì, cancella tutto"}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </section>
        </main>
    );
}
